import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import AuthContext from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FaBriefcase, FaHandHoldingHeart, FaStar, FaPenNib, FaImage, FaPlug,
  FaTrello, FaUsers, FaUserCog, FaHeartbeat, FaCalendarAlt, FaBookOpen,
  FaGlobe, FaSignOutAlt,
} from 'react-icons/fa';
import WorkManager from './WorkManager';
import NonProfitManager from './NonProfitManager';
import HighlightManager from './HighlightManager';
import UserManager from './UserManager';
import ProfileManager from './ProfileManager';
import BlogManager from './BlogManager';
import IntegrationsManager from './IntegrationsManager';
import HeroManager from './HeroManager';
import TrelloManager from './TrelloManager';
import MealPlanManager from './MealPlanManager';
import RecipeManager from './RecipeManager';
import HealthManager from './health/HealthManager';

const NAV = [
  {
    label: 'Website',
    items: [
      { key: 'hero', title: 'Hero Section', icon: <FaImage />, el: <HeroManager /> },
      { key: 'work', title: 'Work Experience', icon: <FaBriefcase />, el: <WorkManager /> },
      { key: 'nonprofit', title: 'Non-Profits', icon: <FaHandHoldingHeart />, el: <NonProfitManager /> },
      { key: 'highlights', title: 'Highlights', icon: <FaStar />, el: <HighlightManager /> },
      { key: 'blog', title: 'Blog', icon: <FaPenNib />, el: <BlogManager /> },
      { key: 'trello', title: 'Trello', icon: <FaTrello />, el: <TrelloManager /> },
      { key: 'integrations', title: 'Integrations', icon: <FaPlug />, el: <IntegrationsManager /> },
      { key: 'users', title: 'User Management', icon: <FaUsers />, el: <UserManager />, adminOnly: true },
    ],
  },
  {
    label: 'Life',
    items: [
      { key: 'health', title: 'Health', icon: <FaHeartbeat />, el: <HealthManager />, adminOnly: true },
      { key: 'mealplans', title: 'Meal Plans', icon: <FaCalendarAlt />, el: <MealPlanManager /> },
      { key: 'recipes', title: 'Recipes', icon: <FaBookOpen />, el: <RecipeManager /> },
    ],
  },
  {
    label: 'Account',
    items: [{ key: 'profile', title: 'My Profile', icon: <FaUserCog />, el: <ProfileManager /> }],
  },
];

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [active, setActive] = useState(() => localStorage.getItem('adminActiveTool') || 'hero');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.adminOnly || user.role === 'admin'),
  })).filter((g) => g.items.length);

  const allItems = groups.flatMap((g) => g.items);
  const current = allItems.find((i) => i.key === active) || allItems[0];

  const select = (key) => {
    setActive(key);
    localStorage.setItem('adminActiveTool', key);
  };

  return (
    <Container fluid className="py-4 px-lg-4 admin-shell">
      <Row>
        <Col lg={2} md={3} className="mb-3">
          {/* Mobile: compact select + site/logout links */}
          <div className="d-md-none mb-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">Mike Williams</span>
              <span>
                <button type="button" className="btn btn-link btn-sm p-0 me-3" onClick={() => navigate('/')}>View site</button>
                <button type="button" className="btn btn-link btn-sm p-0" onClick={logout}>Log out</button>
              </span>
            </div>
            <Form.Select value={current.key} onChange={(e) => select(e.target.value)}>
              {groups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((i) => (
                    <option key={i.key} value={i.key}>{i.title}</option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
          </div>
          {/* Desktop: grouped rail */}
          <nav className="admin-rail d-none d-md-block">
            <div className="admin-rail-brand">Mike Williams</div>
            {groups.map((g) => (
              <div key={g.label} className="admin-rail-group">
                <div className="admin-rail-label">{g.label}</div>
                {g.items.map((i) => (
                  <button
                    key={i.key}
                    type="button"
                    className={`admin-rail-item${i.key === current.key ? ' active' : ''}`}
                    onClick={() => select(i.key)}
                  >
                    <span className="admin-rail-icon">{i.icon}</span>
                    {i.title}
                  </button>
                ))}
              </div>
            ))}
            <div className="admin-rail-footer">
              <button type="button" className="admin-rail-item" onClick={() => navigate('/')}>
                <span className="admin-rail-icon"><FaGlobe /></span>
                View site
              </button>
              <button type="button" className="admin-rail-item" onClick={logout}>
                <span className="admin-rail-icon"><FaSignOutAlt /></span>
                Log out
              </button>
            </div>
          </nav>
        </Col>
        <Col lg={10} md={9}>
          <div className="d-flex align-items-baseline justify-content-between mb-3">
            <h2 className="mb-0">{current.title}</h2>
            <span className="text-muted small">Signed in as {user.name}</span>
          </div>
          {current.el}
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
